import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy, OnInit, QueryList, ViewChildren, inject } from '@angular/core'
import { MatMenu, MatMenuTrigger } from '@angular/material/menu'
import { Router } from '@angular/router'
import * as Sentry from '@sentry/angular'
import { head, identity, split } from 'lodash-es'
import { BehaviorSubject, combineLatest } from 'rxjs'
import { distinctUntilChanged, filter, map } from 'rxjs/operators'
import { AuthApiService } from 'src/api/auth'
import { HybridRouter } from 'src/app/angularjs/hybrid-router.service'
import { routesByName } from 'src/app/routes'
import { ROUTES } from 'src/app/routes.constants'
import { environment } from 'src/environments/environment'
import { MatchedOfferNotifierService } from 'src/services/matched-offer-notifier'
import { AmplitudeService } from 'src/shared/amplitude/amplitude.service'
import { ModalService } from 'src/shared/modal'
import { MfaSettingsComponent, type MfaSettingsDialogData } from './mfa-settings/mfa-settings.component'
import { PasswordFormService } from './password-form/password-form.service'


@Component({
  selector: 'tc-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class HeaderComponent implements AfterViewInit, OnInit, OnDestroy {
  private AuthApi = inject(AuthApiService)
  private MatchedOfferNotifier = inject(MatchedOfferNotifierService)
  private PasswordForm = inject(PasswordFormService)
  private readonly modal = inject(ModalService)
  private hybrid = inject(HybridRouter)
  private router = inject(Router)
  private cd = inject(ChangeDetectorRef)
  private readonly amplitude = inject(AmplitudeService)

  readonly version = environment.version
  readonly env = environment.env
  readonly enableChartFeature = environment.enableChartFeature
  readonly enableMultipleBids = environment.enableMultipleBids
  readonly WA_16623_ENABLE_MAERSK_PAGE = environment.WA_16623_ENABLE_MAERSK_PAGE
  readonly WA_16870_ENABLE_AI_AGENTS = environment.WA_16870_ENABLE_AI_AGENTS
  readonly WA_22776_ENABLE_USDA_PRICE_REPORT = environment.WA_22776_ENABLE_USDA_PRICE_REPORT
  readonly WA_20077_ENABLE_CARRIER_INTERACTIVE_DASHBOARD = environment.WA_20077_ENABLE_CARRIER_INTERACTIVE_DASHBOARD
  readonly WA_21936_ENABLE_ASYNC_EXPORT_HISTORY = environment.WA_21936_ENABLE_ASYNC_EXPORT_HISTORY
  readonly WA_22404_ENABLE_PROMOTED_OFFERS = environment.WA_22404_ENABLE_PROMOTED_OFFERS
  readonly WA_22580_ENABLE_SETTINGS_ATTACHMENTS = environment.WA_22580_ENABLE_SETTINGS_ATTACHMENTS
  protected readonly enableRouteService = environment.enableRouteService
  protected readonly ROUTES = ROUTES

  @Input()
  theme: 'dark' | 'light'

  newMatchedOffers: number
  username: string
  isNavCollapsed: boolean

  @ViewChildren(MatMenu)
  menus: QueryList<MatMenu>

  @ViewChildren(MatMenuTrigger)
  menuTriggers: QueryList<MatMenuTrigger>

  sentryReplay$ = new BehaviorSubject('')


  constructor() {
    const AuthApi = this.AuthApi;
    const hybrid = this.hybrid;

    this.getCurrentStatePath()
    combineLatest([
      AuthApi.currentUser$.pipe(map(user => user?.role), distinctUntilChanged()),
      hybrid.urlChanged$,
    ]).subscribe(() => {
      this.getCurrentStatePath()
    })
  }


  ngOnInit() {
    // const sub = DealFinancialsListener.dealFinancialsChanged$.subscribe(data =>
    //   console.log('deal Finacials changed', data))
    // $scope.$on('$destroy', () => sub.unsubscribe)

    this.AuthApi.realUser$.pipe(filter(identity)).subscribe(() => this.MatchedOfferNotifier.start())

    this.MatchedOfferNotifier.onChange((amount) => {
      this.newMatchedOffers = amount
      this.cd.markForCheck()
    })
  }

  ngOnDestroy() {
    this.MatchedOfferNotifier.stop()
  }

  ngAfterViewInit() {
    this.menus.forEach(menu => menu.overlayPanelClass = 'theme-nav-menu')
    this.hybrid.urlChanged$.subscribe(() => this.menuTriggers.forEach(trigger => trigger.closeMenu()));
  }

  // getThemeClass() {
  //   if (this.theme === 'dark') return ['bg-dark', 'navbar-dark']
  //   if (this.theme === 'light') return ['bg-light', 'navbar-light']
  //   this.theme = 'dark' // default
  //   return this.getThemeClass()
  // }

  private getCurrentStatePath() {
    const currentUser = this.AuthApi.currentUser
    if (!currentUser) return
    this.username = currentUser.firstname + ' ' + currentUser.lastname
    this.cd.markForCheck()
  }

  changePassword() {
    this.PasswordForm.showChangePassword()
  }

  openMfaSettings() {
    this.modal.openDialog<MfaSettingsComponent, MfaSettingsDialogData, { method: string }>(
      MfaSettingsComponent,
      {},
      { width: '720px' },
    )
  }

  async logout() {
    await this.AuthApi.logout()
    this.router.navigateByUrl('/login')
  }

  canAccess(stateName: string) {
    if (!this.AuthApi.currentUser) return false
    const { role } = this.AuthApi.currentUser
    return routesByName[stateName]?.roles?.includes(role)
  }

  routeUrl(stateName: string) {
    return head(split(routesByName[stateName]?.url || '', '?'))
  }

  async getReplayId() {
    if (this.sentryReplay$.value) return
    const replay = Sentry.getReplay()
    if (!replay) return
    await replay.flush()
    this.sentryReplay$.next(`https://codegence-inc.sentry.io/replays/${replay.getReplayId()}/`)
  }

  goToLink(url: string) {
    window.open(url, "_blank", "noopener,noreferrer");
  }

  track = this.amplitude.track.bind(this.amplitude)
}
